import React from 'react';
import { get, set, find, cloneDeep, isEmpty, merge, concat, debounce, isUndefined, isString } from 'lodash';

// local dependencies
import { ServiceNowService } from 'services/serviceNow';
import { serviceNow as ServiceNowView } from 'ui/views/widgets/serviceNow';
import { FORM_FIELDS, ACTIONS, SERVICE_RESPONSE_STATUS, SIZES, ERROR_RESPONSE_TEMPLATE } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import { overlay } from 'modules/overlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ServiceNow } from 'ui/containers/ServiceNow';
 *
 * // render
 * <ServiceNow propName='propValue'/>
 */
export class ServiceNow extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.__bind();

        this.serviceNowService = new ServiceNowService();

        this.state = {
            ...this.props,
            values: {},
            disableSubmit: true,

            closePopoverOnScrollTrigger: 0
        };
    }

    /**
     * @desc Bind functions
     */
    __bind() {
        this.handleFormFieldChange = this.handleFormFieldChange.bind( this );
        this.handleCategoryChange = this.handleCategoryChange.bind( this );
        this.handleCategoryTypeChange = this.handleCategoryTypeChange.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleNextActionClick = this.handleNextActionClick.bind( this );
        this.handleCategorySubTypeChange = this.handleCategorySubTypeChange.bind( this );
        this.handleCategorySubTypeDetailChange = this.handleCategorySubTypeDetailChange.bind( this );
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
        this.handleFormScroll = debounce( this.handleFormScroll, 500, { leading: true, trailing: false } ).bind( this );
        this.fetchUrlfromPriority = this.fetchUrlfromPriority.bind( this );
        this.handleGrievanceModeChange = this.handleGrievanceModeChange.bind( this );
    }

    /**
     * @desc Executes on scroll event in form
     */
    handleFormScroll() {
        log.debug( 'ServiceNow.handleFormScroll()' );

        // toggle the state of trigger
        this.setState( state => {
            return { closePopoverOnScrollTrigger: state.closePopoverOnScrollTrigger ? 0 : 1 };
        } );
    }

    /**
     * @desc Handles change in form field
     */
    handleFormFieldChange( { values, field } ) {
        log.debug( 'ServiceNow.handleFormFieldChange()' );

        if ( FORM_FIELDS.CATEGORY === field.name && values[ field.name ] !== this.state.values.category ) {
            this.handleCategoryChange( { value: values[ field.name ] } );
        } else if ( FORM_FIELDS.CATEGORY_TYPE === field.name && values[ field.name ] !== this.state.values.categoryType ) {
            this.handleCategoryTypeChange( { value: values[ field.name ] } );
        } else if ( FORM_FIELDS.CATEGORY_SUB_TYPE === field.name && values[ field.name ] !== this.state.values.categorySubType ) {
            this.handleCategorySubTypeChange( { value: values[ field.name ] } );
        } else if( FORM_FIELDS.CATEGORY_SUB_TYPE_DETAIL === field.name && values[ field.name ] !== this.state.values.categorySubTypeDetail ) {
            this.handleCategorySubTypeDetailChange( { value: values[ field.name ] } );
        } else if ( FORM_FIELDS.GRIEVANCE_MODE === field.name ) {
            this.handleGrievanceModeChange( { value: values[ field.name ] } );
        }
    }

    /**
     * @desc Handles change in category field
     */
    handleCategoryChange( { value } ) {
        log.debug( 'ServiceNow.handleCategoryChange()' );

        if ( ! isEmpty( value ) ) {

            // clone current state
            let _state = cloneDeep( this.state );

            // reset state
            _state = {
                ...this.props,
                values: { category: value },
                disableSubmit: true
            };

            // get option object corresponding to selected category
            const categoryOptions = get( _state, 'options.categoryOptions' );
            const selectedCategoryOption = find( categoryOptions, { value } );

            // get options for category type corresponding to selected category
            const categoryTypeOptions = get( selectedCategoryOption, 'options' );

            // set category type options in state
            set( _state, 'options.categoryTypeOptions', categoryTypeOptions );

            // update state
            this.setState( _state );
        }
    }

    /**
     * @desc Handles change in category type field
     */
    handleCategoryTypeChange( { value } ) {
        log.debug( 'ServiceNow.handleCategoryTypeChange()' );

        if ( ! isEmpty( value ) ) {

            // clone current state
            let _state = cloneDeep( this.state );

            // enable button if subtype field is hidden
            let disableSubmit = true;
            if ( get( find( get( _state, 'fields', [] ), { name: FORM_FIELDS.CATEGORY_SUB_TYPE } ), 'config.hide', true ) ) {
                disableSubmit = false;
            }

            // reset state
            _state = {
                ...this.props,
                values: {
                    category: _state.values.category,
                    categoryType: value
                },
                options: _state.options,
                disableSubmit
            };

            // get option object corresponding to selected category type
            const categoryTypeOptions = get( _state, 'options.categoryTypeOptions' );
            const selectedCategoryTypeOption = find( categoryTypeOptions, { value } );

            if( isUndefined( selectedCategoryTypeOption ) ) {
                set( _state, 'values.categoryType', '' );
            }

            // get options for category sub type corresponding to selected category type
            const categorySubTypeOptions = get( selectedCategoryTypeOption, 'options' );

            // set category sub type options in state
            set( _state, 'options.categorySubTypeOptions', categorySubTypeOptions );

            // update state
            this.setState( _state );
        }
    }

    /**
     * @desc Handles change in category sub type field
     */
    handleCategorySubTypeChange( { value } ) {
        log.debug( 'ServiceNow.handleCategorySubTypeChange()' );

        // reset state
        if ( ! isEmpty( value ) ) {

            // clone current state
            let _state = cloneDeep( this.state );

            // enable button if subtype - Detail field is hidden
            let disableSubmit = true;
            if ( get( find( get( _state, 'fields', [] ), { name: FORM_FIELDS.CATEGORY_SUB_TYPE_DETAIL } ), 'config.hide', true ) ) {
                disableSubmit = false;
            }

            // reset state
            _state = {
                ...this.props,
                values: {
                    category: _state.values.category,
                    categoryType: _state.values.categoryType,
                    categorySubType: value
                },
                options: _state.options, //state.options
                disableSubmit
            };

            // get option object corresponding to selected category type
            const subCategoryTypeOptions = get( _state, 'options.categorySubTypeOptions' );
            const selectedSubCategorySubTypeOption = find( subCategoryTypeOptions, { value } );

            if( isUndefined( selectedSubCategorySubTypeOption ) ) {
                set( _state, 'values.categorySubType', '' );
            }
            
            // get options for category sub type corresponding to selected category type
            const subTypeDetailOptions = get( selectedSubCategorySubTypeOption, 'options' );

            // set category sub type options in state
            set( _state, 'options.categorySubTypeDetailOptions', subTypeDetailOptions );

            // update state
            this.setState( _state );
        }
    }

    /**
     * @desc Handles change in category sub type detail field
     */
    handleCategorySubTypeDetailChange( { value } ) {
        log.debug( 'ServiceNow.handleCategorySubTypeDetailChange()' );

        // reset state
        if ( ! isEmpty( value ) ) {
            this.setState( state => {
                return {
                    ...this.props,
                    values: {
                        category: state.values.category,
                        categoryType: state.values.categoryType,
                        categorySubType: state.values.categorySubType,
                        categorySubTypeDetail: value
                    },
                    options: state.options,
                    disableSubmit: false
                };
            } );
        }
    }

    /**
     * @desc Handles action clicks
     */
    handleActionClick( { name, values } ) {
        log.debug( 'ServiceNow.handleActionClick()' );

        if ( ACTIONS.NEXT === name ) {
            this.handleNextActionClick( { values } );
        }
    }

    /**
     * @desc Handles finding 'urls' for every service now form data : recusrsive function
     */
    fetchUrlfromPriority( priorityList = [], values ) {
        
        // If stack is empty
        if( 0 === priorityList.length ) {
            return {};
        }
    
        // Extract top of the stack and remove that object
        const lastItemInStack = priorityList.pop();
    
        if( !isEmpty( values[ lastItemInStack ] ) && isString( values[ lastItemInStack ] ) ) {

            const urlConfigObject = find( this.state.urlMap, { name: values[ lastItemInStack ] }, {} );

            if( !isUndefined( urlConfigObject ) ) {  //if 'url' entry found
               return get( urlConfigObject, 'url', {} );
            } else {
                return this.fetchUrlfromPriority( priorityList, values ); //check using next priority
            }
        } else {
            return this.fetchUrlfromPriority( priorityList, values ); //check using next priority
        }
    }

    /**
     * @desc Handles grievance mode change
     */
     handleGrievanceModeChange( { value } ) {
        log.debug( 'ServiceNow.handleGrievanceModeChange()' );

        // clone current state
        const _state = cloneDeep( this.state );

        // get option object corresponding to selected grievance mode
        const grievanceModeOptions = get( _state, 'options.grievanceModeOptions' );
        const grievanceModeSelectedOption = find( grievanceModeOptions, { value } );

        // get options for mode type corresponding to selected grievance mode
        const modeTypeOptions = get( grievanceModeSelectedOption, 'options' );

        // set mode type options in state
        set( _state, 'options.modeTypeOptions', modeTypeOptions );

        // update state
        this.setState( _state );
    }

    /**
     * @desc Handles next action click
     */
    async handleNextActionClick( { values } ) {
        log.debug( 'ServiceNow.handleNextActionClick()' );

        const formCategoryValues = {
            category: get( this.state, `values.${ FORM_FIELDS.CATEGORY }`, '' ),
            categoryType: get( this.state, `values.${ FORM_FIELDS.CATEGORY_TYPE }`, '' ),
            categorySubType: get( this.state, `values.${ FORM_FIELDS.CATEGORY_SUB_TYPE }`, '' ),
            categorySubTypeDetail: get( this.state, `values.${ FORM_FIELDS.CATEGORY_SUB_TYPE_DETAIL }`, '' )
        };

        const urlFetchingPriority = [ FORM_FIELDS.CATEGORY, FORM_FIELDS.CATEGORY_TYPE, FORM_FIELDS.CATEGORY_SUB_TYPE, FORM_FIELDS.CATEGORY_SUB_TYPE_DETAIL ]; // priority List to find urls "Stack Based"

        const url = this.fetchUrlfromPriority( urlFetchingPriority, formCategoryValues ); // function to find the most matching url for the form data
    
        // show page level preloader
        pageLevelPreloader.show();
        
        // cancel ongoing service
        this.serviceNowService.cancel();

        // get status and widget data corresponding to above selected value
        const { status, data } = await this.serviceNowService.fetchForm( {
            url,
            data: {
                context: this.props.context,
                data: merge( values, formCategoryValues )
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();
        
        // check if status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // clone current state
            const _state = cloneDeep( this.state );

            merge( _state.options, data.options );

            const newValues = cloneDeep( data.values );
            newValues.category = formCategoryValues.category;
            newValues.categoryType = formCategoryValues.categoryType;
            newValues.categorySubType = formCategoryValues.categorySubType;
            newValues.categorySubTypeDetail = formCategoryValues.categorySubTypeDetail;

            _state.values = newValues;

            _state.actions = data.actions;
            _state.fields = concat( this.props.fields, data.fields );

            // set updated state
            this.setState( _state );
        } else {

            // show error overlay
            errorOverlay.open();
        }
    }

    /**
     * @desc Handles form submit
     */
    async handleFormSubmit ( formData ) {
        log.debug( 'ServiceNow.handleFormSubmit()' );

        const context = get( this.props, 'context', {} );
        const successAction = find( this.state.actions, { name: 'submit' } );

        // cancel ongoing service
        this.serviceNowService.cancel();

        // show page preloader
        pageLevelPreloader.show();

        overlay.close();

        const { status, data } = await this.serviceNowService.submitFormData( {
            url: successAction.url,
            data: {
                context,
                data: formData
            }
        } );

        // hide page preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // get sub message from response data
            const subMessage = getLabel( get( data, 'message' ), get( data, 'data' ) );

            // show success overlay
            successOverlay.open( {
                message: getLabel( get( data, 'title' ) ),
                config: {
                    autoClose: false,
                    overlaySize: SIZES.LARGE,
                    messageSize: SIZES.LARGE,
                    subMessage
                }
            } );

        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <ServiceNowView
                { ...this.state }
                onChangeCallBack={ this.handleFormFieldChange }
                onActionClick={ this.handleActionClick }
                onFormSubmit = { this.handleFormSubmit }
                onScroll={ this.handleFormScroll }
            />
        );
    }
}

// set display name
ServiceNow.displayName = 'ServiceNow';

// set default props
ServiceNow.defaultProps = {};

// prop types
ServiceNow.propTypes = {};
